import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, useWindowDimensions } from 'react-native';
import { openSettings } from 'react-native-permissions';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ErrorIcon } from '@/assets/icons/error';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { SBK_BETSLIP_LOG_TAG, useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { BetPlacementErrorCodes, GeoComplyError, SubmitBetError } from '@/feature/betslip-sbk/types';
import { CreateEntryAccuracyError } from '@/hooks/use-entries';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { getGeoComplyErrorMessage } from '@/utils/get-geocomply-error-message';
import { logger } from '@/utils/logging';

const HEADER_FOOTER_HEIGHT = 275;

const BET_PLACEMENT_ERROR_FALLBACK_KEY = 'generic_fallback';
const PREDICTIONS_ERROR_FALLBACK_KEY = 'trade_place_error_fallback';

const ODDS_CHANGE_ERROR_CODES: BetPlacementErrorCodes[] = [
    'custom_odds_are_different_from_db',
    'current_odds_are_different_than_requested',
    'current_odds_are_lower_than_requested',
];

export const useHandleBetSubmissionErrors = () => {
    const { t } = useTranslation(['betslip_sbk', 'common', 'error']);
    const { showInfoSheet } = useAlerts();
    const { height } = useWindowDimensions();
    const safeInsets = useSafeAreaInsets();
    const updateOrFetchNewSgpOdds = useSbkBetSlipStore(state => state.actions.updateOrFetchNewSgpOdds);
    const refreshSelectionOdds = useSbkBetSlipStore(state => state.actions.refreshSelectionOdds);
    const isPredictions = useIsPredictionsProduct();
    const maxContainerHeight = height - safeInsets.top - HEADER_FOOTER_HEIGHT;

    return useCallback(
        (error: unknown) => {
            const fallbackKey = isPredictions ? PREDICTIONS_ERROR_FALLBACK_KEY : BET_PLACEMENT_ERROR_FALLBACK_KEY;
            let title = t(isPredictions ? 'trade_rejected' : 'entry_rejected');
            let description: string | React.ReactNode = t(fallbackKey);
            let handleSecondaryPress;
            let secondaryLabel;

            if (error instanceof GeoComplyError) {
                // keeping this as constant for now to avoid duplication and confusion within Phrase
                title = 'Location failure';
                const errorMessage = getGeoComplyErrorMessage(error.payload) || description;
                // geo comply error varies in length, so we need to wrap it in a ScrollView to ensure it doesn't overflow
                // for specific screen sizes
                description = (
                    <Box maxHeight={maxContainerHeight}>
                        <ScrollView showsVerticalScrollIndicator={showScrollIndicator}>
                            <Text color={ds.colorToken.text.secondary} textAlign="center" variant="bodyMedium">
                                {errorMessage}
                            </Text>
                        </ScrollView>
                    </Box>
                );
            } else if (error instanceof CreateEntryAccuracyError) {
                title = t('common:precise_location_failure');
                // keeping this as constant for now to avoid duplication and confusion within Phrase
                description =
                    'Precise location must be turned on before you can place an entry.\n Go to app Settings → Permissions → Location and allow access and precise location.';
                secondaryLabel = t('common:go_to_settings');
                handleSecondaryPress = openSettings;
            } else if (error instanceof SubmitBetError) {
                const isOddsChange = !!error.code && ODDS_CHANGE_ERROR_CODES.includes(error.code);
                description =
                    isPredictions && isOddsChange
                        ? t('trade_price_adjusted')
                        : t([error.code ?? fallbackKey, fallbackKey], error.params);
                if (error.code === 'huddle_service_odds_are_different_than_requested') {
                    updateOrFetchNewSgpOdds({ forceUpdate: true });
                }
                if (isOddsChange) {
                    refreshSelectionOdds().catch(refreshError => {
                        logger.warn(SBK_BETSLIP_LOG_TAG, 'Error refreshing selection odds', refreshError);
                    });
                }
            }

            showInfoSheet({
                title,
                description,
                buttonLabel: t('common:close'),
                secondaryLabel,
                handleSecondaryPress,
                icon: (
                    <Box mt="s16">
                        <ErrorIcon />
                    </Box>
                ),
            });
        },
        [t, isPredictions, showInfoSheet, maxContainerHeight, updateOrFetchNewSgpOdds, refreshSelectionOdds]
    );
};
